/** aica CommonConstants.UserVerificationStatus.VERIFIED */
export const VERIFIED_USER_STATUS = 'Verified';

export const ACCOUNT_STATUS = { VERIFIED: 'VERIFIED', NOT_ACTIVATED: 'NOT_ACTIVATED' } as const;

const NOT_ACTIVATED_LABEL = 'Not activated';

export const MEERA_BROADCAST_COPY = {
    PAGE_TITLE: 'Meera Broadcasts',
    PAGE_SUBTITLE: 'Send a WhatsApp message from Meera to the partners you pick.',
    SEARCH_PLACEHOLDER: 'Search by name, phone or partner org',
    SHOWING_COUNT: (shown: number, total: number, selected: number) => `Showing ${shown} of ${total} · ${selected} selected`,
    HIDDEN_PARTNERS_NOTE: (count: number) => `${count} partners hidden because they can't be messaged`,
    EMPTY_AUDIENCE: 'No partner users found',
    LOAD_ERROR: 'Could not load partners',
    RETRY: 'Retry',
    COMPOSER_LABEL: 'Message',
    COMPOSER_PLACEHOLDER: 'Write your message. Use [Name] and [Partner org] to personalise it.',
    CHARACTER_COUNT: (used: number, max: number) => `${used}/${max}`,
    PREVIEW_TITLE: 'Preview',
    PREVIEW_PICKER_PLACEHOLDER: 'Pick a selected person',
    PREVIEW_PLACEHOLDER: 'Your message appears here.',
    BROADCAST_BUTTON: (count: number) => `Send to ${count} people`,
    BROADCAST_CONFIRM_TITLE: (count: number, hiddenCount: number) =>
        `Send to ${count} people${hiddenCount > 0 ? ` (${hiddenCount} are not in the current view)` : ''}`,
    BROADCAST_CONFIRM_MESSAGE: (count: number) => `This sends a real WhatsApp message from Meera to ${count} people.`,
    BROADCAST_CONFIRM_LABEL: 'Send now',
    KEEP_TAB_OPEN: 'Keep this tab open until sending finishes.',
    SENDING: 'Sending…',
    SENT: 'Sent',
    FAILED: (message: string) => `Failed: ${message}`,
    FAILED_LABEL: 'Failed',
    UNKNOWN_LABEL: 'Unknown',
    FAILED_FALLBACK: 'Could not send',
    NO_ACCESS: 'You do not have access to send broadcasts',
    UNKNOWN: 'Unknown — check their WhatsApp before resending',
    SELECT_ALL: 'Select all',
    RUN_SUMMARY: (sent: number, failed: number, unknown: number) => `${sent} sent · ${failed} failed · ${unknown} unknown`,
    SHOW_FAILED: 'Show failed',
    MY_PARTNERS: 'My partners',
    NOT_ACTIVATED: NOT_ACTIVATED_LABEL,
    ACCOUNT_STATUS_FILTER_LABEL: 'Account status',
    ACCOUNT_STATUS_LABEL: { [ACCOUNT_STATUS.VERIFIED]: 'Verified', [ACCOUNT_STATUS.NOT_ACTIVATED]: NOT_ACTIVATED_LABEL },
    NOT_ACTIVATED_TOOLTIP: "Account not activated — Meera can't recognise their replies, so they can't be sent a broadcast",
    HEADERS: {
        NAME: 'Name',
        ORG: 'Partner org',
        OWNER: 'Owner',
        PHONE: 'Phone',
        LAST_BROADCAST: 'Last broadcast',
        LAST_ACTIVITY: 'Last WhatsApp activity',
        CHAT: 'Chat',
    },
    VIEW_CHAT: 'View chat',
    HIDE_CHAT: 'Hide chat',
    VIEW_CHAT_WITH: (name: string) => `View chat with ${name}`,
    HIDE_CHAT_WITH: (name: string) => `Hide chat with ${name}`,
    SENT_BY_FILTER_LABEL: 'Sent by',
    SENT_BY: (name: string) => `by ${name}`,
    FILTERS_ARIA_LABEL: 'Filters',
    RECENCY_MODE_LABEL: {
        BROADCAST: {
            ANY: 'Broadcast: any',
            NOT_WITHIN: 'Broadcast: not sent in last',
        },
        ACTIVITY: {
            ANY: 'Activity: any',
            WITHIN: 'Activity: active in last',
            NOT_WITHIN: 'Activity: not active in last',
        },
    },
    RECENCY_DAYS: (days: number) => (days === 1 ? '1 day' : `${days} days`),
    ACTIVITY_LOAD_ERROR: 'Could not load WhatsApp activity',
    ACTIVITY_UNAVAILABLE: '—',
    OWNER_UNKNOWN: '—',
    OWNER_FILTER_LABEL: 'Owner',
    RECENT_BROADCAST_WARNING: (count: number, total: number, age: string) =>
        `${count} of ${total} already got a broadcast from this tool; the most recent was ${age}.`,
};

export const MEERA_CHAT_COPY = {
    READ_ONLY: "Read-only: you can't reply from here.",
    NOT_FOUND: 'No WhatsApp chat with Meera found for this person.',
    LOAD_FAILED: 'Could not load this chat',
    EMPTY_VALUE: '—',
    CLOSE: 'Close chat',
    CANNOT_VIEW: {
        'Cannot view: SHARED_PHONE': "This phone number is shared with another user, so the chat can't be shown.",
        'Cannot view: NO_PHONE': 'This partner has no phone number, so there is no WhatsApp chat to show.',
        'Cannot view: INVALID_PHONE':
            "This partner's phone number isn't a valid Indian mobile number, so there is no WhatsApp chat to show.",
    } as Record<string, string>,
};

export const MAX_MESSAGE_LENGTH = 1000;
export const COMPOSER_ROWS = 8;

export const PLACEHOLDERS = {
    NAME: '[Name]',
    PARTNER_ORG: '[Partner org]',
} as const;
export const INSERT_BUTTONS = [PLACEHOLDERS.NAME, PLACEHOLDERS.PARTNER_ORG];
export const BLANK_NAME_FALLBACK = 'there';

export const SEND_ENDPOINT = '/meeraBroadcast/send';
export const SEND_CONCURRENCY = 3;
export const ACTIVITY_ENDPOINT = '/meeraBroadcast/activity';
export const CHAT_TIMELINE_ENDPOINT = '/meeraBroadcast/chat/timeline';
export const MEERA_BROADCAST_FILTER_SOURCE_KEY = 'MEERA_BROADCAST';
export const FORBIDDEN_HTTP_STATUS = 403;
export const NO_ORGS_FOUND_PREFIX = 'No orgs found';
export const PARTNER_USER_TYPE = 'PARTNER';

export const TEN_DIGIT_PHONE = /^\d{10}$/;
export const SEARCH_TOKEN_SEPARATOR = /[\s,;]+/;
export const PHONE_PUNCTUATION = /[+\-()]/g;
export const DIGITS_ONLY = /^\d+$/;
export const PASTED_PHONE = /^\d{10,}$/;
export const INDIA_COUNTRY_CODES = ['', '91', '+91', 'IND'];
export const INDIA_DIAL_PREFIX = '+91';

export const LIST_STATE = {
    LOADING: 'LOADING',
    READY: 'READY',
    ERROR: 'ERROR',
} as const;

export const SEND_STATE = {
    SENDING: 'SENDING',
    SENT: 'SENT',
    FAILED: 'FAILED',
    UNKNOWN: 'UNKNOWN',
} as const;

/** A row in one of these states is never posted again from this page. */
export const NO_RESEND_STATES: string[] = [SEND_STATE.SENDING, SEND_STATE.SENT, SEND_STATE.UNKNOWN];

export const TABLE_FIELDS = {
    SELECT: 'select',
    NAME: 'name',
    ORG: 'partnerOrgId',
    OWNER: 'organizationOwner',
    LAST_BROADCAST: 'lastBroadcastAt',
    LAST_ACTIVITY: 'lastInboundAt',
    PHONE: 'phone',
    CHAT: 'chat',
} as const;

/** Filter-only drawer column (never shown in the table); the applied key is the sender's email. */
export const SENT_BY_FIELD = 'lastBroadcastByEmail';

/** Filter-only drawer column: which account statuses are listed. */
export const ACCOUNT_STATUS_FIELD = 'accountStatus';
/** Applied on every page load and whenever the Account status card is emptied. */
export const DEFAULT_DRAWER_FILTERS = { [ACCOUNT_STATUS_FIELD]: [ACCOUNT_STATUS.VERIFIED] };

/** The drawer applies the Owner filter under this key instead of its column field. */
export const OWNER_APPLIED_KEY = 'owners.owner';

/** Column widths in rem so the columns plus the checkbox fit the left pane at 1440px without a horizontal scroll. */
export const COLUMN_WIDTHS = {
    SELECT: '6.5rem',
    NAME: '8.5rem',
    ORG: '8.5rem',
    OWNER: '7.5rem',
    LAST_BROADCAST: '10.5rem',
    LAST_ACTIVITY: '11.5rem',
    PHONE: '7rem',
    CHAT: '5.5rem',
} as const;

export const RECENCY_MODE = {
    ANY: 'ANY',
    WITHIN: 'WITHIN',
    NOT_WITHIN: 'NOT_WITHIN',
} as const;

export const RECENCY_DAY_OPTIONS = [1, 2, 3, 7, 14, 30].map((days) => ({ id: days, label: MEERA_BROADCAST_COPY.RECENCY_DAYS(days) }));
export const DEFAULT_RECENCY_DAYS = 7;
export const MS_PER_DAY = 24 * 60 * 60 * 1000;

const recencyOptions = (labels: Partial<Record<keyof typeof RECENCY_MODE, string>>) =>
    Object.entries(labels).map(([id, label]) => ({ id, label }));

export const BROADCAST_RECENCY_OPTIONS = recencyOptions(MEERA_BROADCAST_COPY.RECENCY_MODE_LABEL.BROADCAST);
export const ACTIVITY_RECENCY_OPTIONS = recencyOptions(MEERA_BROADCAST_COPY.RECENCY_MODE_LABEL.ACTIVITY);

export const CHAT_REGION_ID = 'meera-chat-region';
